import { toStoredDesignation } from './designation'
import { isEmailShape } from './reportsEmail'

/** Reports intake (spec 2026-10-10). One module for the four kinds' shapes,
 *  so the wizard, the Worker, the PDF mapping and the emails validate and
 *  describe a report the same way. */

export const REPORT_KINDS = ['ayso_incident', 'referee_incident', 'coach_feedback', 'referee_feedback', 'concern'] as const
export type ReportKind = (typeof REPORT_KINDS)[number]
/** The kinds the public form takes. A Concern is entered by an admin only. */
export const PUBLIC_REPORT_KINDS: ReportKind[] = ['ayso_incident', 'referee_incident', 'coach_feedback', 'referee_feedback']

export const REPORT_REF_PREFIX: Record<ReportKind, string> = {
  ayso_incident: 'IR',
  referee_incident: 'RI',
  coach_feedback: 'CF',
  referee_feedback: 'RF',
  concern: 'CN',
}

export const REPORT_KIND_LABEL: Record<ReportKind, string> = {
  ayso_incident: 'AYSO Incident Report',
  referee_incident: 'Referee incident report',
  coach_feedback: 'Feedback about a coach',
  referee_feedback: 'Feedback about a referee',
  concern: 'Concern',
}

export type ReportStatus = 'new' | 'in_progress' | 'signed' | 'sent_to_ayso' | 'closed' | 'spam'
export const REPORT_STATUS_LABEL: Record<ReportStatus, string> = {
  new: 'New',
  in_progress: 'In progress',
  signed: 'Signed',
  sent_to_ayso: 'Sent to AYSO',
  closed: 'Closed',
  spam: 'Spam',
}
/** A report event's label in History, on the screen and in the Internal
 *  Incident Report. An unknown type prints as itself. */
export const REPORT_EVENT_LABEL: Record<string, string> = {
  submitted: 'Submitted', email_sent: 'Email sent', email_failed: 'Email failed', slack_sent: 'Slack sent', slack_failed: 'Slack failed',
  status: 'Status changed', signed: 'Signed', note: 'Note added', viewed: 'Viewed', downloaded: 'PDF downloaded',
  resolved: 'Person matched', linked: 'Linked to a game', gap_dismissed: 'Injury gap dismissed',
  assigned: 'Assigned', urgent: 'Urgency changed', internal_pdf: 'Internal Incident Report built', internal_pdf_failed: 'Internal Incident Report failed',
  entered: 'Entered by an admin', escalated: 'Escalated', handed_off: 'Handed off', pdf_rebuilt: 'PDF rebuilt',
}
/** The allowed moves (spec 2026-10-11 §2). Signed and Sent to AYSO exist only
 *  for an AYSO report; the Worker checks the kind as well as this table.
 *  Closing needs Action taken; the Worker checks that too. */
export const REPORT_STATUS_NEXT: Record<ReportStatus, ReportStatus[]> = {
  new: ['in_progress', 'closed', 'spam'],
  in_progress: ['signed', 'closed', 'spam', 'new'],
  signed: ['sent_to_ayso', 'closed'],
  sent_to_ayso: ['closed'],
  closed: ['in_progress'],
  spam: ['new'],
}
export const ACTION_TAKEN_MAX = 2000
export type ReportVia = 'public' | 'referee_link' | 'coach_link' | 'card_upload' | 'admin_entry'
export type ReceivedVia = 'web' | 'text' | 'phone' | 'email' | 'in_person' | 'other'
export const RECEIVED_VIA = [
  { key: 'text', label: 'Text message' },
  { key: 'phone', label: 'Phone call' },
  { key: 'email', label: 'Email' },
  { key: 'in_person', label: 'In person' },
  { key: 'other', label: 'Other' },
] as const
export type LocationKind = 'game' | 'practice' | 'other'

export type PersonRef =
  | { kind: 'roster'; team: string; childId: string }
  | { kind: 'jersey'; team: string; jersey: number }
  | { kind: 'free'; text: string }

export interface Submitter { name: string; email: string; phone: string | null; role: string }

/** A game the platform does not list, typed in by the filer. At least one team is set. */
export interface UnlistedGame { home: string | null; away: string | null; field: string | null }

export interface ReportLocation {
  kind: LocationKind
  /** Game: the listed game. Null for an unlisted game. */
  gameId: string | null
  /** Game: a game typed in. Exactly one of gameId and unlisted is set for a game. */
  unlisted?: UnlistedGame | null
  /** Practice: the team (stored designation, e.g. B10-01). */
  team: string | null
  /** YYYY-MM-DD */
  date: string
  /** HH:MM, 24-hour; null when unknown. */
  time: string | null
  /** Other: where it happened. Practice: optional field. */
  text: string | null
}

type Opt = { key: string; label: string }
const opts = <T extends readonly Opt[]>(o: T) => o

export const AYSO_INCIDENT_TYPES = opts([
  { key: 'injury', label: 'Injury or illness' },
  { key: 'threats', label: 'Threats' },
  { key: 'fights', label: 'Fights' },
  { key: 'property_damage', label: 'Property damage' },
  { key: 'police', label: 'Calls to police' },
  { key: 'other', label: 'Other' },
] as const)
export const AYSO_PARTY_ROLES = opts([
  { key: 'player', label: 'Player' },
  { key: 'official', label: 'Official' },
  { key: 'coach', label: 'Coach' },
  { key: 'spectator', label: 'Spectator' },
  { key: 'volunteer', label: 'Volunteer' },
  { key: 'other', label: 'Other' },
] as const)
export const AYSO_BODY_PARTS = opts([
  { key: 'ankle', label: 'Ankle' }, { key: 'knee', label: 'Knee' }, { key: 'leg', label: 'Leg' },
  { key: 'foot', label: 'Foot' }, { key: 'toe', label: 'Toe' }, { key: 'arm', label: 'Arm' },
  { key: 'hand', label: 'Hand' }, { key: 'shoulder', label: 'Shoulder' }, { key: 'wrist', label: 'Wrist' },
  { key: 'finger', label: 'Finger' }, { key: 'eye', label: 'Eye' }, { key: 'ear', label: 'Ear' },
  { key: 'nose', label: 'Nose' }, { key: 'head', label: 'Head' }, { key: 'tooth', label: 'Tooth' },
  { key: 'back', label: 'Back' }, { key: 'neck', label: 'Neck' }, { key: 'internal', label: 'Internal' },
  { key: 'no_injury', label: 'No injury' }, { key: 'other', label: 'Other' },
] as const)
export const AYSO_INJURY_TYPES = opts([
  { key: 'abrasion', label: 'Abrasion' }, { key: 'burn', label: 'Burn' }, { key: 'cardiac', label: 'Cardiac' },
  { key: 'cold', label: 'Cold injury' }, { key: 'concussion', label: 'Concussion' }, { key: 'contusion', label: 'Contusion' },
  { key: 'dislocation', label: 'Dislocation' }, { key: 'foreign_body', label: 'Foreign body' },
  { key: 'fracture', label: 'Fracture' }, { key: 'heat', label: 'Heat exhaustion' },
  { key: 'laceration', label: 'Laceration' }, { key: 'nausea', label: 'Nausea' }, { key: 'pain', label: 'Pain' },
  { key: 'seizures', label: 'Seizures' }, { key: 'sting_bite', label: 'Sting or bite' }, { key: 'strain', label: 'Strain' },
  { key: 'sprain', label: 'Sprain' }, { key: 'covid', label: 'COVID-19' },
] as const)
export const AYSO_SURFACES = opts([
  { key: 'dirt', label: 'Dirt' }, { key: 'grass', label: 'Grass' }, { key: 'turf', label: 'Turf' }, { key: 'indoor', label: 'Indoor' },
] as const)
export const AYSO_TIMINGS = opts([
  { key: 'before', label: 'Before the game or event' },
  { key: 'during', label: 'During the game or event' },
  { key: 'after', label: 'After the game or event' },
] as const)
export const AYSO_AREAS = opts([
  { key: 'concession', label: 'Concession area' }, { key: 'parking', label: 'Parking lot' }, { key: 'restrooms', label: 'Restrooms' },
] as const)
export const AYSO_CAUSES = opts([
  { key: 'collision', label: 'Collision (participant or spectator)' },
  { key: 'struck_object', label: 'Struck by a falling or flying object' },
  { key: 'goal', label: 'Struck by or fell into a goal' },
  { key: 'animal', label: 'Animal or insect bite or sting' },
  { key: 'slip_fall', label: 'Slip or fall' },
  { key: 'assault_sexual', label: 'Assault, sexual' },
  { key: 'assault_non_sexual', label: 'Assault, non-sexual' },
  { key: 'property_damage', label: 'Property damage' },
] as const)
export const AYSO_DISPOSITIONS = opts([
  { key: 'not_needed', label: 'No treatment needed' },
  { key: 'refused', label: 'Patient refused treatment' },
  { key: 'to_doctor', label: 'To a doctor' },
  { key: 'to_hospital', label: 'To a hospital or clinic' },
  { key: 'to_parent', label: 'Released to a parent' },
  { key: 'personal_vehicle', label: 'Left in a personal vehicle' },
  { key: 'region_recommended', label: 'Region recommended further care' },
  { key: 'parent_requested', label: 'Patient or parent requested further care' },
] as const)

/** The Typeform's IFAB lists (Typeform GjC4Pyed, read 2026-10-10). */
export const CAUTION_REASONS = opts([
  { key: 'delay', label: 'Delaying the restart of play' },
  { key: 'dissent', label: 'Dissent by word or action' },
  { key: 'entering_leaving', label: "Entering, re-entering, or deliberately leaving the field without the referee's permission" },
  { key: 'distance', label: 'Failing to respect the required distance at a restart' },
  { key: 'persistent', label: 'Persistent offenses' },
  { key: 'unsporting', label: 'Unsporting behavior' },
  { key: 'review_signal', label: 'Excessively using the TV review signal' },
] as const)
export const SEND_OFF_REASONS = opts([
  { key: 'dogso', label: 'Denying an obvious goal-scoring opportunity' },
  { key: 'serious_foul', label: 'Serious foul play' },
  { key: 'spitting', label: 'Biting or spitting' },
  { key: 'violent', label: 'Violent conduct' },
  { key: 'language', label: 'Offensive, insulting, or abusive language' },
  { key: 'second_caution', label: 'Second caution' },
] as const)

export const FEEDBACK_RATINGS = opts([
  { key: 'great', label: 'Great' }, { key: 'good', label: 'Good' }, { key: 'fair', label: 'Fair' },
  { key: 'marginal', label: 'Marginal' }, { key: 'poor', label: 'Poor' },
] as const)

type Key<T extends readonly Opt[]> = T[number]['key']

export interface AysoInjured {
  lastName: string; firstName: string; mi: string | null
  /** YYYY-MM-DD */
  dob: string | null
  aysoId: string | null
  phone: string | null
  address: string | null; city: string | null; state: string | null; zip: string | null
  parentLast: string | null; parentFirst: string | null; parentAddress: string | null
  /** Up to three. */
  contacts: { email: string | null; cell: string | null }[]
  insurance: 'yes' | 'no' | 'unknown'
  insuranceDetail: string | null
}

export interface AysoIncidentAnswers {
  incidentTypes: Key<typeof AYSO_INCIDENT_TYPES>[]
  party: Key<typeof AYSO_PARTY_ROLES>
  person: PersonRef
  injured: AysoInjured
  bodyParts: Key<typeof AYSO_BODY_PARTS>[]
  injuryTypes: Key<typeof AYSO_INJURY_TYPES>[]
  surface: Key<typeof AYSO_SURFACES> | null
  timing: Key<typeof AYSO_TIMINGS> | null
  areas: Key<typeof AYSO_AREAS>[]
  causes: Key<typeof AYSO_CAUSES>[]
  dispositions: Key<typeof AYSO_DISPOSITIONS>[]
  policeReport: 'yes' | 'no'
  policeReportNo: string | null
  officer: string | null
  narrative: string
  /** Up to three. */
  witnesses: { name: string; address: string | null; phone: string | null }[]
  reporterPosition: string
}

export interface RefereeIncident {
  type: 'caution' | 'send_off' | 'injury'
  minute: number | null
  person: PersonRef
  /** A CAUTION_REASONS key for a caution, a SEND_OFF_REASONS key for a send-off, null for an injury. */
  reason: string | null
  note: string | null
}
export interface RefereeIncidentAnswers {
  incidents: RefereeIncident[]
  description: string
  otherOfficials: string | null
}
export interface CoachFeedbackAnswers {
  /** Null off a game: a practice or somewhere else has no sides. */
  side: 'home' | 'visitor' | null
  position: 'head' | 'assistant' | 'unsure'
  name: string | null
  rating: Key<typeof FEEDBACK_RATINGS>
  comments: string
  contactMe: boolean
}
export interface RefereeFeedbackAnswers {
  position: 'CR' | 'AR1' | 'AR2' | 'unsure'
  name: string | null
  rating: Key<typeof FEEDBACK_RATINGS>
  comments: string
  contactMe: boolean
}
/** An admin-entered complaint that fits no other kind: a parent, a
 *  spectator, a field (spec 2026-10-11 §3). Every "about" field is optional. */
export interface ConcernAnswers {
  about: { name: string | null; role: Key<typeof AYSO_PARTY_ROLES> | null; team: string | null }
  description: string
}
export type ReportAnswers = AysoIncidentAnswers | RefereeIncidentAnswers | CoachFeedbackAnswers | RefereeFeedbackAnswers | ConcernAnswers

export interface ReportSubmission {
  kind: ReportKind
  clientId: string
  location: ReportLocation
  submitter: Submitter
  answers: ReportAnswers
}

// ── Validation ───────────────────────────────────────────────────────────────
// Hand-rolled (this repo has no schema library): each reader returns the value
// or throws Bad(path). `validateReport` turns the first throw into { field }.

class Bad extends Error { constructor(readonly field: string) { super(field) } }

type R = Record<string, unknown>
const obj = (v: unknown, f: string): R => {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) throw new Bad(f)
  return v as R
}
const str = (v: unknown, f: string, max: number, min = 1): string => {
  if (typeof v !== 'string') throw new Bad(f)
  const s = v.trim()
  if (s.length < min || s.length > max) throw new Bad(f)
  return s
}
const optStr = (v: unknown, f: string, max: number): string | null =>
  v == null || (typeof v === 'string' && v.trim() === '') ? null : str(v, f, max)
const oneOf = <T extends string>(v: unknown, f: string, allowed: readonly T[]): T => {
  if (typeof v !== 'string' || !(allowed as readonly string[]).includes(v)) throw new Bad(f)
  return v as T
}
const optOneOf = <T extends string>(v: unknown, f: string, allowed: readonly T[]): T | null =>
  v == null ? null : oneOf(v, f, allowed)
const keys = <T extends readonly Opt[]>(o: T) => o.map((x) => x.key) as Key<T>[]
const manyOf = <T extends string>(v: unknown, f: string, allowed: readonly T[], max = allowed.length): T[] => {
  if (!Array.isArray(v) || v.length > max) throw new Bad(f)
  const out = v.map((x) => {
    if (typeof x !== 'string' || !(allowed as readonly string[]).includes(x)) throw new Bad(f)
    return x as T
  })
  return [...new Set(out)]
}
const bool = (v: unknown, f: string): boolean => {
  if (typeof v !== 'boolean') throw new Bad(f)
  return v
}
const int = (v: unknown, f: string, lo: number, hi: number): number => {
  if (typeof v !== 'number' || !Number.isInteger(v) || v < lo || v > hi) throw new Bad(f)
  return v
}
const isoDate = (v: unknown, f: string): string => {
  const s = str(v, f, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) throw new Bad(f)
  return s
}
const hhmm = (v: unknown, f: string): string | null => {
  if (v == null) return null
  const s = str(v, f, 5)
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(s)) throw new Bad(f)
  return s
}
/** A stored team id. Shape is loose on purpose: production holds ids such as
 *  `B5Uu5-81` and `Shaw TT B12U`. Whether the team exists is the server's job. */
const designation = (v: unknown, f: string): string => {
  const s = str(v, f, 60)
  if (!/^[^\u0000-\u001f\u007f]{1,60}$/.test(s)) throw new Bad(f)
  return s
}

/** Free-text caps. Generous for a person typing; bounded for a public endpoint. */
export const REPORT_TEXT_MAX = { short: 120, line: 300, narrative: 4000 } as const

function person(v: unknown, f: string): PersonRef {
  const o = obj(v, f)
  const kind = oneOf(o.kind, `${f}.kind`, ['roster', 'jersey', 'free'] as const)
  if (kind === 'roster') return { kind, team: designation(o.team, `${f}.team`), childId: str(o.childId, `${f}.childId`, 64) }
  if (kind === 'jersey') return { kind, team: designation(o.team, `${f}.team`), jersey: int(o.jersey, `${f}.jersey`, 0, 999) }
  return { kind, text: str(o.text, `${f}.text`, REPORT_TEXT_MAX.line) }
}

function submitter(v: unknown, admin: boolean): Submitter {
  const o = obj(v, 'submitter')
  const raw = typeof o.email === 'string' ? o.email.trim() : o.email
  let email = ''
  if (!(admin && (raw === '' || raw == null))) {
    email = str(raw, 'submitter.email', 254)
    if (!isEmailShape(email)) throw new Bad('submitter.email')
  }
  return {
    name: str(o.name, 'submitter.name', REPORT_TEXT_MAX.short),
    email: email.toLowerCase(),
    phone: optStr(o.phone, 'submitter.phone', 40),
    role: str(o.role, 'submitter.role', REPORT_TEXT_MAX.short),
  }
}

function unlistedGame(v: unknown): UnlistedGame {
  const u = obj(v, 'location.unlisted')
  const g: UnlistedGame = {
    home: optStr(u.home, 'location.unlisted.home', REPORT_TEXT_MAX.line),
    away: optStr(u.away, 'location.unlisted.away', REPORT_TEXT_MAX.line),
    field: optStr(u.field, 'location.unlisted.field', REPORT_TEXT_MAX.line),
  }
  if (!g.home && !g.away) throw new Bad('location.unlisted')
  return g
}

function location(v: unknown): ReportLocation {
  const o = obj(v, 'location')
  const lk = oneOf(o.kind, 'location.kind', ['game', 'practice', 'other'] as const)
  const hasUnlisted = o.unlisted != null
  if (lk === 'game' && hasUnlisted && o.gameId != null) throw new Bad('location.unlisted')
  const loc: ReportLocation = {
    kind: lk,
    gameId: lk === 'game' && !hasUnlisted ? str(o.gameId, 'location.gameId', 128) : null,
    unlisted: lk === 'game' && hasUnlisted ? unlistedGame(o.unlisted) : null,
    team: lk === 'practice' ? designation(o.team, 'location.team') : null,
    date: isoDate(o.date, 'location.date'),
    time: hhmm(o.time, 'location.time'),
    text: lk === 'other' ? str(o.text, 'location.text', REPORT_TEXT_MAX.line) : optStr(o.text, 'location.text', REPORT_TEXT_MAX.line),
  }
  return loc
}

function aysoAnswers(v: unknown): AysoIncidentAnswers {
  const a = obj(v, 'answers')
  const i = obj(a.injured, 'answers.injured')
  const contacts = Array.isArray(i.contacts) ? i.contacts : []
  if (contacts.length > 3) throw new Bad('answers.injured.contacts')
  const witnesses = Array.isArray(a.witnesses) ? a.witnesses : []
  if (witnesses.length > 3) throw new Bad('answers.witnesses')
  const incidentTypes = manyOf(a.incidentTypes, 'answers.incidentTypes', keys(AYSO_INCIDENT_TYPES))
  if (incidentTypes.length === 0) throw new Bad('answers.incidentTypes')
  return {
    incidentTypes,
    party: oneOf(a.party, 'answers.party', keys(AYSO_PARTY_ROLES)),
    person: person(a.person, 'answers.person'),
    injured: {
      lastName: str(i.lastName, 'answers.injured.lastName', REPORT_TEXT_MAX.short),
      firstName: str(i.firstName, 'answers.injured.firstName', REPORT_TEXT_MAX.short),
      mi: optStr(i.mi, 'answers.injured.mi', 4),
      dob: i.dob == null || i.dob === '' ? null : isoDate(i.dob, 'answers.injured.dob'),
      aysoId: optStr(i.aysoId, 'answers.injured.aysoId', 40),
      phone: optStr(i.phone, 'answers.injured.phone', 40),
      address: optStr(i.address, 'answers.injured.address', REPORT_TEXT_MAX.line),
      city: optStr(i.city, 'answers.injured.city', REPORT_TEXT_MAX.short),
      state: optStr(i.state, 'answers.injured.state', 20),
      zip: optStr(i.zip, 'answers.injured.zip', 12),
      parentLast: optStr(i.parentLast, 'answers.injured.parentLast', REPORT_TEXT_MAX.short),
      parentFirst: optStr(i.parentFirst, 'answers.injured.parentFirst', REPORT_TEXT_MAX.short),
      parentAddress: optStr(i.parentAddress, 'answers.injured.parentAddress', REPORT_TEXT_MAX.line),
      contacts: contacts.map((c, n) => {
        const co = obj(c, `answers.injured.contacts.${n}`)
        return { email: optStr(co.email, `answers.injured.contacts.${n}.email`, 254), cell: optStr(co.cell, `answers.injured.contacts.${n}.cell`, 40) }
      }),
      insurance: oneOf(i.insurance, 'answers.injured.insurance', ['yes', 'no', 'unknown'] as const),
      insuranceDetail: optStr(i.insuranceDetail, 'answers.injured.insuranceDetail', REPORT_TEXT_MAX.line),
    },
    bodyParts: manyOf(a.bodyParts, 'answers.bodyParts', keys(AYSO_BODY_PARTS)),
    injuryTypes: manyOf(a.injuryTypes, 'answers.injuryTypes', keys(AYSO_INJURY_TYPES)),
    surface: optOneOf(a.surface, 'answers.surface', keys(AYSO_SURFACES)),
    timing: optOneOf(a.timing, 'answers.timing', keys(AYSO_TIMINGS)),
    areas: manyOf(a.areas, 'answers.areas', keys(AYSO_AREAS)),
    causes: manyOf(a.causes, 'answers.causes', keys(AYSO_CAUSES)),
    dispositions: manyOf(a.dispositions, 'answers.dispositions', keys(AYSO_DISPOSITIONS)),
    policeReport: oneOf(a.policeReport, 'answers.policeReport', ['yes', 'no'] as const),
    policeReportNo: optStr(a.policeReportNo, 'answers.policeReportNo', 60),
    officer: optStr(a.officer, 'answers.officer', REPORT_TEXT_MAX.line),
    narrative: str(a.narrative, 'answers.narrative', REPORT_TEXT_MAX.narrative),
    witnesses: witnesses.map((w, n) => {
      const wo = obj(w, `answers.witnesses.${n}`)
      return {
        name: str(wo.name, `answers.witnesses.${n}.name`, REPORT_TEXT_MAX.short),
        address: optStr(wo.address, `answers.witnesses.${n}.address`, REPORT_TEXT_MAX.line),
        phone: optStr(wo.phone, `answers.witnesses.${n}.phone`, 40),
      }
    }),
    reporterPosition: str(a.reporterPosition, 'answers.reporterPosition', REPORT_TEXT_MAX.short),
  }
}

function refereeAnswers(v: unknown): RefereeIncidentAnswers {
  const a = obj(v, 'answers')
  if (!Array.isArray(a.incidents) || a.incidents.length === 0 || a.incidents.length > 20) throw new Bad('answers.incidents')
  const incidents = a.incidents.map((x, n): RefereeIncident => {
    const f = `answers.incidents.${n}`
    const o = obj(x, f)
    const type = oneOf(o.type, `${f}.type`, ['caution', 'send_off', 'injury'] as const)
    const reasons = type === 'caution' ? keys(CAUTION_REASONS) : type === 'send_off' ? keys(SEND_OFF_REASONS) : []
    return {
      type,
      minute: o.minute == null ? null : int(o.minute, `${f}.minute`, 0, 130),
      person: person(o.person, `${f}.person`),
      reason: type === 'injury' ? null : oneOf(o.reason, `${f}.reason`, reasons as string[]),
      note: optStr(o.note, `${f}.note`, REPORT_TEXT_MAX.line),
    }
  })
  return {
    incidents,
    description: typeof a.description === 'string' ? str(a.description, 'answers.description', REPORT_TEXT_MAX.narrative, 0) : '',
    otherOfficials: optStr(a.otherOfficials, 'answers.otherOfficials', REPORT_TEXT_MAX.line),
  }
}

function coachFeedback(v: unknown, atGame: boolean): CoachFeedbackAnswers {
  const a = obj(v, 'answers')
  return {
    side: atGame ? oneOf(a.side, 'answers.side', ['home', 'visitor'] as const) : null,
    position: oneOf(a.position, 'answers.position', ['head', 'assistant', 'unsure'] as const),
    name: optStr(a.name, 'answers.name', REPORT_TEXT_MAX.short),
    rating: oneOf(a.rating, 'answers.rating', keys(FEEDBACK_RATINGS)),
    comments: str(a.comments, 'answers.comments', REPORT_TEXT_MAX.narrative),
    contactMe: bool(a.contactMe, 'answers.contactMe'),
  }
}

function refereeFeedback(v: unknown): RefereeFeedbackAnswers {
  const a = obj(v, 'answers')
  return {
    position: oneOf(a.position, 'answers.position', ['CR', 'AR1', 'AR2', 'unsure'] as const),
    name: optStr(a.name, 'answers.name', REPORT_TEXT_MAX.short),
    rating: oneOf(a.rating, 'answers.rating', keys(FEEDBACK_RATINGS)),
    comments: str(a.comments, 'answers.comments', REPORT_TEXT_MAX.narrative),
    contactMe: bool(a.contactMe, 'answers.contactMe'),
  }
}

function concernAnswers(v: unknown): ConcernAnswers {
  const a = obj(v, 'answers')
  const about = a.about == null ? {} : obj(a.about, 'answers.about')
  return {
    about: {
      name: optStr(about.name, 'answers.about.name', REPORT_TEXT_MAX.short),
      role: about.role == null || about.role === '' ? null : oneOf(about.role, 'answers.about.role', keys(AYSO_PARTY_ROLES)),
      team: about.team == null || about.team === '' ? null : designation(about.team, 'answers.about.team'),
    },
    description: str(a.description, 'answers.description', REPORT_TEXT_MAX.narrative),
  }
}

export function validateReport(
  body: unknown, opts: { admin?: boolean } = {},
): { ok: true; value: ReportSubmission } | { ok: false; field: string } {
  try {
    const o = obj(body, 'body')
    const kind = oneOf(o.kind, 'kind', opts.admin ? REPORT_KINDS : PUBLIC_REPORT_KINDS)
    const clientId = str(o.clientId, 'clientId', 64)
    const loc = location(o.location)
    const sub = submitter(o.submitter, !!opts.admin)
    const answers =
      kind === 'ayso_incident' ? aysoAnswers(o.answers)
      : kind === 'referee_incident' ? refereeAnswers(o.answers)
      : kind === 'coach_feedback' ? coachFeedback(o.answers, loc.kind === 'game')
      : kind === 'concern' ? concernAnswers(o.answers)
      : refereeFeedback(o.answers)
    return { ok: true, value: { kind, clientId, location: loc, submitter: sub, answers } }
  } catch (e) {
    if (e instanceof Bad) return { ok: false, field: e.field }
    throw e
  }
}

// ── Display ─────────────────────────────────────────────────────────────────

const labelOf = (list: readonly Opt[], key: string | null | undefined): string =>
  list.find((x) => x.key === key)?.label ?? ''
/** A label mid-sentence: the first letter lowercased unless it opens with an
 *  acronym ("AYSO Incident Report" keeps its capitals). */
export const lowerFirst = (s: string) => (/^[A-Z]{2}/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1))

/** The one-line category used in Slack, the email subject line and the
 *  Reports list. ⚠️ NEVER A NAME: Slack is outside the platform's RBAC. */
export function reportCategorySummary(kind: ReportKind, answers: ReportAnswers): string {
  if (kind === 'ayso_incident') {
    const a = answers as AysoIncidentAnswers
    const first = a.incidentTypes[0]
    const party = a.party === 'other' ? 'someone else' : (() => {
      const l = lowerFirst(labelOf(AYSO_PARTY_ROLES, a.party))
      return l ? `${/^[aeiou]/.test(l) ? 'an' : 'a'} ${l}` : 'someone'
    })()
    const line = first === 'injury'
      ? `Injury or illness to ${party}`
      : `${first === 'other' ? 'Incident' : labelOf(AYSO_INCIDENT_TYPES, first) || 'Incident'} involving ${party}`
    const more = a.incidentTypes.length - 1
    return more > 0 ? `${line} (+${more} more)` : line
  }
  if (kind === 'referee_incident') {
    const a = answers as RefereeIncidentAnswers
    const count = (t: RefereeIncident['type']) => a.incidents.filter((x) => x.type === t).length
    const parts: string[] = []
    const c = count('caution'), s = count('send_off'), i = count('injury')
    if (c) parts.push(`${c} caution${c === 1 ? '' : 's'}`)
    if (s) parts.push(`${s} send-off${s === 1 ? '' : 's'}`)
    if (i) parts.push(`${i} injur${i === 1 ? 'y' : 'ies'}`)
    return parts.join(', ')
  }
  if (kind === 'coach_feedback') {
    const a = answers as CoachFeedbackAnswers
    const who = a.position === 'head' ? 'Head coach' : a.position === 'assistant' ? 'Assistant coach' : 'Coach'
    return a.side ? `${who}, ${a.side} team: ${labelOf(FEEDBACK_RATINGS, a.rating)}` : `${who}: ${labelOf(FEEDBACK_RATINGS, a.rating)}`
  }
  if (kind === 'concern') {
    const a = answers as ConcernAnswers
    const l = lowerFirst(labelOf(AYSO_PARTY_ROLES, a.about.role))
    return l ? `Concern about ${/^[aeiou]/.test(l) ? 'an' : 'a'} ${l}` : 'Concern'
  }
  const a = answers as RefereeFeedbackAnswers
  const who = a.position === 'unsure' ? 'Referee' : a.position === 'CR' ? 'Center referee' : a.position
  return `${who}: ${labelOf(FEEDBACK_RATINGS, a.rating)}`
}

/** `#5 on B10-20`, `Sam Lee (B10-20)` once resolved, or the typed text. The
 *  caller passes `resolvedName` only on admin screens. The team goes through
 *  `toStoredDesignation` because a report filed before the #881 cutover keeps
 *  the old form in its answers, which the migration did not rewrite. */
export function personRefText(p: PersonRef, resolvedName?: string | null): string {
  if (p.kind === 'free') return p.text
  const team = toStoredDesignation(p.team)
  if (p.kind === 'jersey') return resolvedName ? `#${p.jersey} on ${team} (${resolvedName})` : `#${p.jersey} on ${team}`
  return resolvedName ? `${resolvedName} (${team})` : `A player on ${team}`
}

/** Set at submit (spec 2026-10-11 §2): an AYSO report whose injured person
 *  was released to a hospital or clinic. An admin can set or clear it later. */
export function urgentAtSubmit(s: Pick<ReportSubmission, 'kind' | 'answers'>): boolean {
  return s.kind === 'ayso_incident' && (s.answers as AysoIncidentAnswers).dispositions?.includes('to_hospital') === true
}

/** An injury in a report: always copied to safety@ (spec 2026-10-12 B4). */
export function reportHasInjury(kind: ReportKind, answers: ReportAnswers): boolean {
  if (kind === 'ayso_incident') return (answers as AysoIncidentAnswers).incidentTypes?.includes('injury') === true
  if (kind === 'referee_incident') return (answers as RefereeIncidentAnswers).incidents?.some((i) => i.type === 'injury') === true
  return false
}

/** A coach item, which a Division Coach Administrator may read (spec
 *  2026-10-12 A4): coach feedback, or any report whose subject is a coach. */
export function isCoachItem(kind: ReportKind, subjectRoles: string[]): boolean {
  return kind === 'coach_feedback' || subjectRoles.some((r) => r === 'coach' || r === 'assistant_coach')
}

export function slackPermalink(channel: string, ts: string): string {
  return `https://slack.com/archives/${channel}/p${ts.replace('.', '')}`
}
